import React, { useState, useEffect } from "react";
import { Link } from 'gatsby';
import { useLocation } from '@gatsbyjs/reach-router';
import RequestDemoModal from "./RequestDemoModal";
import {downloadAppStoreUrl} from '../../constants/layout';
import ReactGA from "react-ga4";

const logoIcon: string = require('../../assets/images/logo.svg').default;
const starIcon: string = require('../../assets/images/star-icon.svg').default;
const whiteLogoIcon: string = require('../../assets/images/whiteLogo.svg').default;
const appleStoreIcon: string = require('../../assets/images/app-store.svg').default;
const mobileMenuIcon: string = require('../../assets/images/mobile-menu-icon.svg').default;
const mobileMenuCloseIcon: string = require('../../assets/images/mobile-menu-close-icon.svg').default;
const phoneCallIcon: string = require('../../assets/images/call.svg').default;

interface PageChild {
  name: string;
  link: string;
  hasIcon?: boolean;
  isNew?: boolean;
}

interface Pages {
  name: string;
  link: string;
  hasIcon?: boolean;
  isNew?: boolean;
  children?: PageChild[];
}

const normalizePath = (path: string): string =>
  path !== '/' && path.endsWith('/') ? path.slice(0, -1) : path;

const isNavGroupActive = (pathname: string, page: Pages): boolean => {
  if (!page.children?.length) return false;
  const current = normalizePath(pathname);
  return (
    current === normalizePath(page.link) ||
    page.children.some((c) => normalizePath(c.link) === current)
  );
};

const Header = () => {
  const location = useLocation();

  const pages: Pages[] = [
    {
      name: 'Company',
      link: '/',
    },
    {
      name: "What's New?",
      link: '/news',
      hasIcon: true,
    },
    {
      name: 'Pricing',
      link: '/pricing',
    },
    {
      name: 'Services',
      link: '/services'
    },
    {
      name: 'For Dealers',
      link: '/dealers'
    },
    {
      name: 'For Notaries',
      link: '/notaries',
      children: [
        {
          name: 'Find Out More',
          link: '/find-out-more',
        },
      ],
    },
    {
      name: 'Content Hub',
      link: 'https://info.mavsign.com/mavsign-content-hub/'
    },
    {
      name: 'Contact us',
      link: '/contact-us'
    },
  ];

  //logo scroll when active
  const [navbarLogo, setNavbarLogo] = useState(logoIcon);
  const [navbar, setNavbar] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isMegaMenuOpen, setIsMegaMenuOpen] = useState(false);

  //logo scroll function
  const changeHeader = () => {
    if (window.scrollY >= 55) {
      setNavbarLogo(whiteLogoIcon);
      setNavbar(true);
    } else {
      setNavbarLogo(logoIcon);
      setNavbar(false);
    }
  }

  const toggleModal = () => setIsModalOpen(!isModalOpen);

  const closeMegaMenu = () => setIsMegaMenuOpen(false);

  useEffect(() => {
    changeHeader();
    // adding the event when scroll change Header
    window.addEventListener("scroll", changeHeader);
  })

  const handleRequestDemoClick = () => {
    // Track the event when the "Request Demo" button is clicked
    ReactGA.event({
      category: 'Button',
      action: 'request_demo_event_header',
      label: 'Request Demo Button - Header'
    });
    
    setIsModalOpen(true);
    closeMegaMenu();
  }

  const handleLoginMav2 = () => {
    ReactGA.event({
      category: 'Button',
      action: 'login_MAV2_event',
      label: 'Login to MAV2 Button'
    });

    closeMegaMenu();
  }

  return (
    <React.Fragment>
      <header id="page-topbar" className='header'>
        <div className={"navbar-header justify-content-around " + (navbar ? "sticky-header" : "")}>
          <div className="d-flex">
            <div className="navbar-brand-box">
              <Link to="/" className='logo'>
                <span className="logo-lg">
                  <img src={navbarLogo} alt="mavsign logo" />
                </span>
              </Link>
            </div>

            {/* Mobile menu  */}
            <div className="mobile-menu-wrapper">
              <button className="mobile-menu-icon" onClick={() => setIsMegaMenuOpen(!isMegaMenuOpen)}>
                <img src={mobileMenuIcon} className="icon open-icon"/>
              </button>
              <div className={"mobile-menu " + (isMegaMenuOpen ? 'is-open' : 'is-close')}>
                <div className="mobile-menu-links">
                  <div className="menu-header">
                    <button onClick={closeMegaMenu} className="close-button">
                      <img src={mobileMenuCloseIcon} className="icon close-icon"/>
                    </button>
                    <a onClick={handleLoginMav2} href={process.env.GATSBY_AUTH_URL} target="_blank" rel="noopener noreferrer" className="btn btn-sm me-3 px-3 login-btn">
                      Log In
                    </a>
                  </div>
                  <ul className="menu-list list-unstyled ">
                    {pages.map((page, index) => (
                      <li key={index}>
                        <Link
                          to={page.link}
                          activeClassName="active"
                          onClick={closeMegaMenu}
                        >
                          {page.name}
                          {page.hasIcon && <img src={starIcon} alt="star" className="star-icon" />}
                          {page.isNew && <span className="ms-1 badge badge-pill badge-warning">New</span>}
                        </Link>
                        {page.children?.length ? (
                          <ul className="menu-list-nested list-unstyled">
                            {page.children.map((child, childIndex) => (
                              <li key={childIndex}>
                                <Link
                                  to={child.link}
                                  activeClassName="active"
                                  onClick={closeMegaMenu}
                                >
                                  {child.name}
                                  {child.hasIcon && <img src={starIcon} alt="star" className="star-icon" />}
                                  {child.isNew && <span className="ms-1 badge badge-pill badge-warning">New</span>}
                                </Link>
                              </li>
                            ))}
                          </ul>
                        ) : null}
                      </li>
                    ))}
                  </ul>
                  <div className="menu-footer">
                    <button onClick={handleRequestDemoClick} className={"btn btn-sm px-3 request-demo-btn w-100"}>Request Demo</button>
                    <a href={downloadAppStoreUrl.url}
                        target="_blank" 
                        rel="noopener noreferrer" 
                        className="app-store-btn"
                        onClick={closeMegaMenu}
                      >
                        <img src={appleStoreIcon} alt="mavsign logo" />
                    </a>
                  </div>
                  <div className="phone-call d-flex">
                    <img src={phoneCallIcon} className="me-3" alt="phone-call-icon" />
                    <div className="phone-call-info">
                      <div className="phone-call-title">Get in contact with us</div>
                      <a onClick={closeMegaMenu} href="tel:+1 (877) 708-4383" className="phone-call-number">+1 (877) 708-4383</a>
                    </div>
                  </div>
                </div>
                <button className="mobile-menu-overlay" onClick={closeMegaMenu}/>
              </div>
            </div>
          </div>

          <div className='custom-menu d-flex align-items-center'>
              {pages.map((page: Pages, index: number) =>
                page.children?.length ? (
                  <div key={index} className="header-nav-dropdown me-3 d-inline-block position-relative">
                    <Link
                      to={page.link}
                      className={`header-link${isNavGroupActive(location.pathname, page) ? ' header-link-active' : ''}`}
                    >
                      {page.hasIcon && <img src={starIcon} alt="star" className="star-icon" />}
                      {page.name}
                      {page.isNew && <span className="ms-1 badge badge-pill badge-warning">New</span>}
                    </Link>
                    <ul className="header-submenu list-unstyled" role="menu">
                      {page.children.map((child, childIndex) => (
                        <li key={childIndex} role="none">
                          <Link
                            role="menuitem"
                            to={child.link}
                            className="header-submenu-link"
                            activeClassName="header-submenu-link-active"
                          >
                            {child.name}
                            {child.hasIcon && <img src={starIcon} alt="star" className="star-icon" />}
                            {child.isNew && <span className="ms-1 badge badge-pill badge-warning">New</span>}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : (
                  <Link key={index} to={page.link} className="header-link me-3" activeClassName='header-link-active'>
                    {page.hasIcon && <img src={starIcon} alt="star" className="star-icon" />}
                    {page.name}
                    {page.isNew && <span className="ms-1 badge badge-pill badge-warning">New</span>}
                  </Link>
                )
              )}
          </div>
          <div className='custom-buttons-wrapper d-flex'>
            <a onClick={handleLoginMav2} href={process.env.GATSBY_AUTH_URL} target="_blank" rel="noopener noreferrer" className="btn btn-sm me-3 px-3 login-btn">
              Log In
            </a>
            <button onClick={handleRequestDemoClick} className={"btn btn-sm px-3 request-demo-btn"}>Request Demo</button>
          </div>
          <div className='custom-mobile-logo'>
            <Link to="/" className='logo'>
              <img src={logoIcon} alt="mavsign logo" />
            </Link>
          </div>
        </div>
      </header>
      <RequestDemoModal isOpen={isModalOpen} toggle={toggleModal} closeModal={() => setIsModalOpen(false)} />
    </React.Fragment>
  );
};

export default Header;

